// ============================================
// OIA Layer 4: Commitment Registry
// Extracted from HemoLogix Terms of Service v1.0
//
// 80 measurable commitments (CMT-001 → CMT-080)
// Organized by TOS section (§1–§13)
// ============================================

import type { Commitment } from '../../types/oia';

// ── Scope Activity ID Reference ──
// S-001: User registration / account creation
// S-002: Lab result upload & storage
// S-003: AI biomarker comparison & insight generation
// S-004: Telehealth consultation (Tier 2)
// S-005: Prescription issuance (Tier 2)
// S-006: Supplement sales & fulfillment (Tier 3)
// S-007: Payment processing
// S-008: Marketing communications
// S-009: Data export & portability
// S-010: Account deletion & data erasure
// S-011: Consent collection & management
// S-012: Regulatory compliance operations
// S-013: IP licensing & platform technology
// S-014: Dispute resolution & legal

export const COMMITMENT_REGISTRY: Commitment[] = [

  // ════════════════════════════════════════════
  // SECTION 1: ACCEPTANCE AND RE-CONSENT
  // ════════════════════════════════════════════

  {
    id: 'CMT-001',
    scopeActivities: ['S-001', 'S-011'],
    requirements: [],
    obligation: '',
    commitment: 'Material changes to TOS require affirmative re-consent, not merely continued use.',
    category: 'consent_management',
    type: 'conditional',
    sla: null,
    specifics: 'When HemoLogix makes material changes to the Terms of Service, users must affirmatively re-consent. Continued use alone does not constitute acceptance of material changes. This exceeds the industry standard of "continued use = acceptance."',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§1', language: 'Material changes require affirmative re-consent; continued use alone does not constitute acceptance.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'System blocks access to changed features until re-consent captured',
      suspenders: 'Audit log confirms re-consent timestamp for each user after material change',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['affirmative re-consent', 'material changes', 'not continued use', 'explicit acceptance'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy', '1.3-consumer-health-data-policy'],
  },

  {
    id: 'CMT-002',
    scopeActivities: ['S-001', 'S-011'],
    requirements: [],
    obligation: '',
    commitment: '30-day window for re-consent before account suspension.',
    category: 'sla_enforcement',
    type: 'conditional',
    sla: '30 calendar days',
    specifics: 'After material changes are published and users are notified, they have a 30-day window to provide affirmative re-consent. If re-consent is not received within 30 days, the account is suspended (not deleted).',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§1', language: '30-day re-consent window before account suspension.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Automated timer triggers suspension exactly at day 30 without re-consent',
      suspenders: 'Daily cron checks pending re-consent queue for overdue accounts',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['30-day window', 're-consent', 'account suspension', 'material changes'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-003',
    scopeActivities: ['S-001', 'S-008', 'S-011'],
    requirements: [],
    obligation: '',
    commitment: 'Email notification plus prominent in-app notice for material changes.',
    category: 'notification',
    type: 'conditional',
    sla: null,
    specifics: 'Material changes to the TOS must be communicated via two channels: (1) email notification to the registered email address, and (2) prominent notice within the application. Both channels must be used for every material change.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§1', language: 'We will notify you via email and prominent notice within the Service.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Email delivery confirmation logs for each user on material change event',
      suspenders: 'In-app banner display confirmed via UI event logging',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['email notification', 'prominent notice', 'material changes', 'dual-channel'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  // ════════════════════════════════════════════
  // SECTION 2: ELIGIBILITY AND AGE
  // ════════════════════════════════════════════

  {
    id: 'CMT-004',
    scopeActivities: ['S-001'],
    requirements: [],
    obligation: '',
    commitment: 'Age gate restricts service to users 18 years or older.',
    category: 'safety',
    type: 'ongoing',
    sla: null,
    specifics: 'The Service is available only to individuals who are at least 18 years of age. An age gate must be present at registration. No exceptions for parental consent.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§2', language: 'You must be at least 18 years of age to use this Service.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Registration form requires date of birth; system rejects under-18',
      suspenders: 'Periodic audit of user age data for anomalies',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['age gate', '18 years', 'eligibility', 'minor restriction'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy', '1.3-consumer-health-data-policy'],
  },

  {
    id: 'CMT-005',
    scopeActivities: ['S-001', 'S-010'],
    requirements: [],
    obligation: '',
    commitment: 'Misrepresented age triggers termination and full data deletion within 24 hours.',
    category: 'safety',
    type: 'conditional',
    sla: '24 hours',
    specifics: 'If HemoLogix discovers a user misrepresented their age and is under 18, the account is immediately terminated and all associated data is permanently deleted within 24 hours of discovery.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§2', language: 'Terminate account and delete all data within 24 hours upon discovering age misrepresentation.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Automated deletion pipeline triggered on minor-detected flag with 24-hour SLA timer',
      suspenders: 'Admin review confirms deletion completion with proof hash',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['age misrepresentation', '24-hour deletion', 'terminate', 'minor data'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy', '1.3-consumer-health-data-policy'],
  },

  {
    id: 'CMT-006',
    scopeActivities: ['S-001'],
    requirements: [],
    obligation: '',
    commitment: 'No minor accounts — service is not available for parents/guardians on behalf of minors.',
    category: 'safety',
    type: 'prohibitive',
    sla: null,
    specifics: 'The Service is designed solely for adult personal use. Parents and guardians may not create accounts on behalf of minors. There is no "managed minor" account type.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§2', language: 'The Service is not intended for use by or on behalf of minors.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Registration flow has no guardian/minor pathway; explicit adult-only declaration',
      suspenders: 'Content and UX review confirms no minor-oriented features',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['no minor accounts', 'adult only', 'not on behalf of minors', 'no guardian accounts'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  // ════════════════════════════════════════════
  // SECTION 3: DESCRIPTION OF SERVICES
  // ════════════════════════════════════════════

  {
    id: 'CMT-007',
    scopeActivities: ['S-001', 'S-002', 'S-003'],
    requirements: [],
    obligation: '',
    commitment: 'Tier 1 provided at no charge to registered users.',
    category: 'financial',
    type: 'ongoing',
    sla: null,
    specifics: 'Tier 1 (lab upload and AI comparison) is free for all registered users. No subscription fee, no freemium gate on core Tier 1 features.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§3', language: 'Tier 1 is provided at no charge to registered users.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Billing system confirms no charges generated for Tier 1 usage',
      suspenders: 'Monthly audit of invoices confirms zero-charge for Tier 1-only users',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['Tier 1', 'no charge', 'free', 'registered users'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-008',
    scopeActivities: ['S-003'],
    requirements: [],
    obligation: '',
    commitment: 'AI is educational comparison tool only — not a medical device or diagnostic.',
    category: 'entity_integrity',
    type: 'ongoing',
    sla: null,
    specifics: 'The AI-powered biomarker comparison tool is for educational and informational purposes only. It is not a medical device, does not provide diagnoses, and should not be relied upon for medical decision-making.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§3', language: 'AI-powered features are educational comparison tools only, not medical devices or diagnostics.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'All AI output includes disclaimer banner; no diagnostic language in prompts',
      suspenders: 'Quarterly prompt audit confirms no diagnostic framing',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['educational only', 'not medical device', 'not diagnostic', 'comparison tool', 'informational'],
    appearsInTemplates: ['1.1-terms-of-service', '1.7-telehealth-informed-consent'],
  },

  {
    id: 'CMT-009',
    scopeActivities: ['S-003'],
    requirements: [],
    obligation: '',
    commitment: 'AI content may contain errors — explicit disclaimer on all AI output.',
    category: 'safety',
    type: 'ongoing',
    sla: null,
    specifics: 'Every AI-generated insight, comparison, or recommendation must carry an explicit disclaimer that AI content may contain errors, inaccuracies, or incomplete information.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§3', language: 'AI-generated content may contain errors and should not be considered medical advice.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'UI component renders disclaimer on every AI output; cannot be hidden or dismissed permanently',
      suspenders: 'Automated UI test confirms disclaimer presence on all AI output pages',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['may contain errors', 'AI disclaimer', 'not medical advice', 'inaccuracies'],
    appearsInTemplates: ['1.1-terms-of-service', '1.6-acceptable-use-policy'],
  },

  {
    id: 'CMT-010',
    scopeActivities: ['S-004'],
    requirements: [],
    obligation: '',
    commitment: 'Telehealth delivered via OpenLoop provider network — HemoLogix does not practice medicine.',
    category: 'entity_integrity',
    type: 'ongoing',
    sla: null,
    specifics: 'Telehealth consultations are provided through the OpenLoop provider network. HemoLogix is a technology platform that facilitates the connection; it does not practice medicine, employ physicians, or control clinical decisions.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§3', language: 'Telehealth consultations are provided through the OpenLoop provider network.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'All telehealth sessions routed through OpenLoop API; no direct provider employment',
      suspenders: 'MSA with OpenLoop on file and reviewed annually',
    },
    priority: 'P0',
    owner: 'attorney',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['OpenLoop', 'provider network', 'does not practice medicine', 'technology platform'],
    appearsInTemplates: ['1.1-terms-of-service', '1.7-telehealth-informed-consent', '4.1-msa-llc-pc'],
  },

  {
    id: 'CMT-011',
    scopeActivities: ['S-004', 'S-007'],
    requirements: [],
    obligation: '',
    commitment: 'Consultation fee is $49 per telehealth consultation.',
    category: 'financial',
    type: 'ongoing',
    sla: null,
    specifics: 'Each Tier 2 telehealth consultation is priced at $49. This fee is clearly disclosed before the user books. Any fee changes require updated disclosure.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§3', language: 'The consultation fee is $49 per consultation.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Checkout flow displays $49 fee; Stripe price object matches',
      suspenders: 'Monthly billing audit confirms all consultation charges match $49',
    },
    priority: 'P1',
    owner: 'system',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['$49', 'consultation fee', 'per consultation', 'Tier 2'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-012',
    scopeActivities: ['S-004', 'S-005'],
    requirements: [],
    obligation: '',
    commitment: 'No guarantee of prescription — clinical judgment of treating provider prevails.',
    category: 'entity_integrity',
    type: 'ongoing',
    sla: null,
    specifics: 'A telehealth consultation does not guarantee that a prescription will be issued. The treating provider exercises independent clinical judgment. HemoLogix does not influence prescribing decisions.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§3', language: 'No guarantee of prescription; clinical judgment of the treating provider prevails.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Pre-consultation disclosure includes no-guarantee language; user must acknowledge',
      suspenders: 'Audit log confirms acknowledgment captured before each consultation',
    },
    priority: 'P0',
    owner: 'provider',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['no guarantee', 'prescription', 'clinical judgment', 'treating provider'],
    appearsInTemplates: ['1.1-terms-of-service', '1.7-telehealth-informed-consent'],
  },

  {
    id: 'CMT-013',
    scopeActivities: ['S-004', 'S-011'],
    requirements: [],
    obligation: '',
    commitment: 'Separate Telehealth Informed Consent required before first consultation.',
    category: 'consent_management',
    type: 'conditional',
    sla: null,
    specifics: 'Before a user can proceed with their first Tier 2 telehealth consultation, they must review and sign a separate Telehealth Informed Consent document. This is distinct from the general TOS acceptance.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§3', language: 'Separate Telehealth Informed Consent required before first consultation.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'System blocks consultation booking until telehealth consent is captured',
      suspenders: 'Consent record audit confirms telehealth consent exists for all Tier 2 users',
    },
    priority: 'P0',
    owner: 'system',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['telehealth informed consent', 'separate consent', 'before first consultation', 'Tier 2'],
    appearsInTemplates: ['1.1-terms-of-service', '1.7-telehealth-informed-consent'],
  },

  {
    id: 'CMT-014',
    scopeActivities: ['S-006'],
    requirements: [],
    obligation: '',
    commitment: 'FDA disclaimer on all supplements — not intended to diagnose, treat, cure, or prevent any disease.',
    category: 'regulatory_specific',
    type: 'ongoing',
    sla: null,
    specifics: 'All supplement product pages, labels, and communications must include the standard FDA disclaimer: "These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease."',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§3', language: 'FDA disclaimer: not intended to diagnose, treat, cure, or prevent any disease.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Product page template includes FDA disclaimer component; cannot be removed',
      suspenders: 'Quarterly content audit confirms FDA disclaimer on all Tier 3 product pages',
    },
    priority: 'P0',
    owner: 'system',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['FDA disclaimer', 'not intended to diagnose', 'supplement', 'not evaluated'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-015',
    scopeActivities: ['S-006'],
    requirements: [],
    obligation: '',
    commitment: 'Consult healthcare provider before starting supplements (pregnant, nursing, medications, allergies).',
    category: 'safety',
    type: 'ongoing',
    sla: null,
    specifics: 'All supplement products must include a warning to consult a healthcare provider before use, especially for individuals who are pregnant, nursing, taking medications, or have known allergies.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§3', language: 'Consult your healthcare provider before starting any supplement, especially if pregnant, nursing, taking medications, or have allergies.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Supplement checkout flow includes healthcare provider consultation warning',
      suspenders: 'Product page audit confirms warning presence on all supplement listings',
    },
    priority: 'P0',
    owner: 'system',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['consult healthcare provider', 'pregnant', 'nursing', 'medications', 'allergies'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  // ════════════════════════════════════════════
  // SECTION 4: MEDICAL AND AI DISCLAIMERS
  // ════════════════════════════════════════════

  {
    id: 'CMT-016',
    scopeActivities: ['S-003', 'S-004', 'S-005'],
    requirements: [],
    obligation: '',
    commitment: 'HemoLogix is a technology platform and does not practice medicine (CPOM firewall).',
    category: 'entity_integrity',
    type: 'ongoing',
    sla: null,
    specifics: 'HemoLogix LLC operates as a technology platform. It does not practice medicine, make clinical decisions, or direct patient care. This is the corporate practice of medicine (CPOM) firewall. All medical services are provided by independently licensed providers through OpenLoop.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§4', language: 'HemoLogix is a technology platform and does not practice medicine.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Entity registry enforces CPOM labels on all documents; no medical verbs attributed to LLC',
      suspenders: 'Annual attorney review of all user-facing content for CPOM compliance',
    },
    priority: 'P0',
    owner: 'attorney',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['technology platform', 'does not practice medicine', 'CPOM', 'firewall'],
    appearsInTemplates: ['1.1-terms-of-service', '1.7-telehealth-informed-consent', '4.1-msa-llc-pc', '4.2-medical-director-agreement'],
  },

  {
    id: 'CMT-017',
    scopeActivities: ['S-004', 'S-005'],
    requirements: [],
    obligation: '',
    commitment: 'HemoLogix does not employ physicians or control clinical decisions.',
    category: 'entity_integrity',
    type: 'prohibitive',
    sla: null,
    specifics: 'HemoLogix does not employ, supervise, or direct physicians. Clinical decisions are made independently by licensed providers through the OpenLoop network. HemoLogix has no authority over clinical outcomes.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§4', language: 'HemoLogix does not employ physicians or control clinical decisions.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'No W-2 or 1099 relationships with physicians in payroll system',
      suspenders: 'MSA with OpenLoop confirms independent contractor status of providers',
    },
    priority: 'P0',
    owner: 'attorney',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['does not employ', 'physicians', 'independent clinical decisions', 'no control'],
    appearsInTemplates: ['1.1-terms-of-service', '1.7-telehealth-informed-consent', '4.1-msa-llc-pc'],
  },

  {
    id: 'CMT-018',
    scopeActivities: ['S-004'],
    requirements: [],
    obligation: '',
    commitment: 'No provider-patient relationship with HemoLogix — only with treating provider via Tier 2.',
    category: 'entity_integrity',
    type: 'ongoing',
    sla: null,
    specifics: 'Use of HemoLogix does not create a provider-patient relationship between the user and HemoLogix. Any provider-patient relationship exists solely between the user and the treating provider accessed through the Tier 2 telehealth service.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§4', language: 'No provider-patient relationship is created between you and HemoLogix.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'All user-facing content reviewed for relationship-implying language',
      suspenders: 'Telehealth consent explicitly states relationship is with provider, not HemoLogix',
    },
    priority: 'P0',
    owner: 'attorney',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['no provider-patient relationship', 'HemoLogix', 'treating provider only', 'Tier 2'],
    appearsInTemplates: ['1.1-terms-of-service', '1.7-telehealth-informed-consent'],
  },

  {
    id: 'CMT-019',
    scopeActivities: ['S-003'],
    requirements: [],
    obligation: '',
    commitment: 'AI limitations disclosed — probabilistic, may be incomplete, outdated, or incorrect.',
    category: 'safety',
    type: 'ongoing',
    sla: null,
    specifics: 'The AI system\'s limitations are explicitly disclosed: outputs are probabilistic in nature and may be incomplete, outdated, or incorrect. Users are informed that AI results should not replace professional medical judgment.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§4', language: 'AI outputs are probabilistic and may be incomplete, outdated, or incorrect.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'AI output UI includes limitations disclosure; rendered as non-dismissable component',
      suspenders: 'Prompt engineering review confirms no overconfident language patterns',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['probabilistic', 'incomplete', 'outdated', 'incorrect', 'AI limitations'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-020',
    scopeActivities: ['S-003', 'S-004'],
    requirements: [],
    obligation: '',
    commitment: 'Service not for emergencies — call 911 for emergencies.',
    category: 'safety',
    type: 'ongoing',
    sla: null,
    specifics: 'The Service is not designed for medical emergencies. Prominent notice directs users to call 911 or go to the nearest emergency room for emergency situations. This notice must appear in the app and in relevant communications.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§4', language: 'This Service is not for emergencies. If you are experiencing a medical emergency, call 911.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Emergency disclaimer banner present on all health-related screens',
      suspenders: 'Automated content scan confirms 911 language on required pages',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['not for emergencies', 'call 911', 'emergency room', 'medical emergency'],
    appearsInTemplates: ['1.1-terms-of-service', '1.7-telehealth-informed-consent'],
  },

  {
    id: 'CMT-021',
    scopeActivities: ['S-004'],
    requirements: [],
    obligation: '',
    commitment: 'Telehealth limitations disclosed — no physical exam, reliance on patient-provided information.',
    category: 'safety',
    type: 'ongoing',
    sla: null,
    specifics: 'Users are informed that telehealth has inherent limitations: providers cannot perform physical examinations, and clinical decisions rely on information provided by the patient. These limitations are disclosed before the first consultation.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§4', language: 'Telehealth has limitations including the inability to perform physical examinations.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Telehealth consent form includes limitations section; must be acknowledged',
      suspenders: 'Consent record audit confirms limitations acknowledgment for all Tier 2 users',
    },
    priority: 'P0',
    owner: 'system',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['telehealth limitations', 'no physical exam', 'patient-provided information', 'inherent limitations'],
    appearsInTemplates: ['1.1-terms-of-service', '1.7-telehealth-informed-consent'],
  },

  // ════════════════════════════════════════════
  // SECTION 5: CONSENT MODEL
  // ════════════════════════════════════════════

  {
    id: 'CMT-022',
    scopeActivities: ['S-011'],
    requirements: [],
    obligation: '',
    commitment: '7 granular consent purposes: 4 required and 3 optional.',
    category: 'consent_management',
    type: 'ongoing',
    sla: null,
    specifics: 'Consent is collected for 7 distinct purposes. Required: (1) account creation & service delivery, (2) lab data processing, (3) AI analysis, (4) communications about account/service. Optional: (5) marketing communications, (6) de-identified research, (7) third-party sharing for service improvement.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§5', language: 'Seven granular consent purposes: four required for service operation and three optional.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Consent UI presents all 7 purposes with individual toggles; required marked as non-optional',
      suspenders: 'Consent record schema enforces 7 distinct boolean fields per user',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['7 consent purposes', 'granular consent', '4 required', '3 optional', 'per-purpose'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy', '1.3-consumer-health-data-policy'],
  },

  {
    id: 'CMT-023',
    scopeActivities: ['S-001', 'S-011'],
    requirements: [],
    obligation: '',
    commitment: 'Required consent withdrawal triggers account deactivation.',
    category: 'consent_management',
    type: 'conditional',
    sla: null,
    specifics: 'If a user withdraws any of the 4 required consents, the account is deactivated because the service cannot operate without them. The user is informed of this consequence before withdrawal is processed.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§5', language: 'Withdrawal of required consent will result in account deactivation.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Consent withdrawal flow shows deactivation warning; processes deactivation on confirmation',
      suspenders: 'Audit log confirms no active accounts with missing required consents',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['required consent', 'withdrawal', 'account deactivation', 'cannot operate'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  {
    id: 'CMT-024',
    scopeActivities: ['S-011'],
    requirements: [],
    obligation: '',
    commitment: 'Optional consent withdrawal has no impact on service availability.',
    category: 'consent_management',
    type: 'conditional',
    sla: null,
    specifics: 'Users may withdraw any of the 3 optional consents (marketing, research, third-party sharing) at any time without any impact on their access to or quality of the core service.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§5', language: 'Withdrawal of optional consent will not affect your access to or use of the Service.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Service access logic does not check optional consent flags',
      suspenders: 'Test suite verifies full service access with all optional consents withdrawn',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['optional consent', 'no service impact', 'withdrawal', 'unaffected access'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  {
    id: 'CMT-025',
    scopeActivities: ['S-008', 'S-011'],
    requirements: [],
    obligation: '',
    commitment: 'Marketing opt-out via unsubscribe link or email to privacy@hemologix.com.',
    category: 'consent_management',
    type: 'ongoing',
    sla: null,
    specifics: 'Users can opt out of marketing communications via two methods: (1) clicking the unsubscribe link in any marketing email, or (2) sending an email to privacy@hemologix.com.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§5', language: 'Opt out of marketing via unsubscribe link or email to privacy@hemologix.com.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'All marketing emails include functional unsubscribe link; privacy@ inbox monitored',
      suspenders: 'Monthly test of unsubscribe flow confirms opt-out processing',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['marketing opt-out', 'unsubscribe', 'privacy@hemologix.com', 'opt out'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  {
    id: 'CMT-026',
    scopeActivities: ['S-011'],
    requirements: [],
    obligation: '',
    commitment: 'Research consent withdrawal via email with specific subject line.',
    category: 'consent_management',
    type: 'conditional',
    sla: null,
    specifics: 'Users can withdraw research consent by emailing privacy@hemologix.com with a specific subject line (e.g., "Withdraw Research Consent"). The system processes the withdrawal and confirms.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§5', language: 'Withdraw research consent by emailing privacy@hemologix.com with subject line "Withdraw Research Consent."' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Email parsing rule detects research withdrawal subject line and triggers consent update',
      suspenders: 'Consent dashboard shows research consent status change with email reference',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['research consent', 'withdrawal', 'email', 'subject line', 'privacy@'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  {
    id: 'CMT-027',
    scopeActivities: ['S-011'],
    requirements: [],
    obligation: '',
    commitment: 'Third-party sharing consent withdrawal via email with specific subject line.',
    category: 'consent_management',
    type: 'conditional',
    sla: null,
    specifics: 'Users can withdraw third-party sharing consent by emailing privacy@hemologix.com with a specific subject line (e.g., "Withdraw Third-Party Sharing Consent"). The system processes the withdrawal and confirms.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§5', language: 'Withdraw third-party sharing consent by emailing privacy@hemologix.com with subject line "Withdraw Third-Party Sharing Consent."' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Email parsing rule detects sharing withdrawal subject line and triggers consent update',
      suspenders: 'Downstream data sharing pipelines check consent flag before each share event',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['third-party sharing', 'consent withdrawal', 'email', 'subject line', 'privacy@'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy', '1.3-consumer-health-data-policy'],
  },

  {
    id: 'CMT-028',
    scopeActivities: ['S-011'],
    requirements: [],
    obligation: '',
    commitment: 'Consent withdrawal processed within 10 business days with email confirmation.',
    category: 'sla_enforcement',
    type: 'conditional',
    sla: '10 business days',
    specifics: 'Any consent withdrawal request is processed within 10 business days. Upon completion, the user receives an email confirmation that their consent has been updated.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§5', language: 'Consent withdrawal processed within 10 business days; confirmation email sent upon completion.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Consent withdrawal queue with 10-business-day SLA timer and auto-escalation',
      suspenders: 'Weekly report of pending withdrawals flags any approaching SLA deadline',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['10 business days', 'consent withdrawal', 'email confirmation', 'processed'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  {
    id: 'CMT-029',
    scopeActivities: ['S-011'],
    requirements: [],
    obligation: '',
    commitment: 'Material data processing changes require affirmative re-consent.',
    category: 'consent_management',
    type: 'conditional',
    sla: null,
    specifics: 'If HemoLogix materially changes how it processes user data (new purposes, new third parties, new categories of data), affirmative re-consent is required from each affected user before the new processing begins.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§5', language: 'Material changes to data processing require affirmative re-consent.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Data processing change triggers re-consent campaign; new processing blocked until consent captured',
      suspenders: 'Privacy officer sign-off required before any data processing change is deployed',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['data processing changes', 'affirmative re-consent', 'material changes', 'new processing'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy', '1.3-consumer-health-data-policy'],
  },

  {
    id: 'CMT-030',
    scopeActivities: ['S-001', 'S-011'],
    requirements: [],
    obligation: '',
    commitment: '30-day re-consent window before suspension for data processing changes.',
    category: 'sla_enforcement',
    type: 'conditional',
    sla: '30 calendar days',
    specifics: 'After notification of material data processing changes, users have 30 days to provide re-consent. If re-consent is not received within the window, the account is suspended (not deleted) until re-consent is obtained.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§5', language: '30-day window for re-consent to data processing changes before account suspension.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Automated 30-day timer from notification date; suspension triggered on expiration',
      suspenders: 'Daily pending re-consent report identifies accounts approaching deadline',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['30-day window', 're-consent', 'data processing', 'suspension'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  // ════════════════════════════════════════════
  // SECTION 6: ACCOUNT MANAGEMENT AND DELETION
  // ════════════════════════════════════════════

  {
    id: 'CMT-031',
    scopeActivities: ['S-010'],
    requirements: [],
    obligation: '',
    commitment: 'Account deletion available via account settings or email to support@hemologix.com.',
    category: 'data_rights',
    type: 'ongoing',
    sla: null,
    specifics: 'Users can request account deletion through two methods: (1) self-service via account settings in the application, or (2) by emailing support@hemologix.com. Both methods must be available at all times.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§6', language: 'Request account deletion via account settings or by emailing support@hemologix.com.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Account settings page includes delete button; support@ inbox monitored for deletion requests',
      suspenders: 'Monthly test of both deletion pathways confirms functionality',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['account deletion', 'account settings', 'support@hemologix.com', 'self-service'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy', '1.3-consumer-health-data-policy'],
  },

  {
    id: 'CMT-032',
    scopeActivities: ['S-010'],
    requirements: [],
    obligation: '',
    commitment: '24-hour cooling-off period before deletion begins.',
    category: 'sla_enforcement',
    type: 'conditional',
    sla: '24 hours',
    specifics: 'After a deletion request is submitted, there is a 24-hour cooling-off period during which the user can cancel the request and retain their account. Deletion processing does not begin until this period expires.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§6', language: '24-hour cooling-off period before account deletion begins processing.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Deletion queue holds requests for 24 hours; cancel option available during window',
      suspenders: 'Audit log confirms no deletion processing occurred within 24 hours of request',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['24-hour cooling-off', 'cancellation window', 'before deletion', 'cooling-off period'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  {
    id: 'CMT-033',
    scopeActivities: ['S-010'],
    requirements: [],
    obligation: '',
    commitment: 'Deletion completed within 30 days after cooling-off period.',
    category: 'sla_enforcement',
    type: 'conditional',
    sla: '30 calendar days',
    specifics: 'After the 24-hour cooling-off period expires, account deletion is completed within 30 calendar days. All user data is permanently removed from active systems within this window.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§6', language: 'Account deletion completed within 30 days after cooling-off period.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Deletion pipeline tracks 30-day SLA from cooling-off expiration; auto-escalation at day 25',
      suspenders: 'Monthly audit confirms no deletion requests exceed 30-day SLA',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['30-day deletion', 'completed within 30 days', 'permanent removal', 'after cooling-off'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy', '1.3-consumer-health-data-policy'],
  },

  {
    id: 'CMT-034',
    scopeActivities: ['S-009'],
    requirements: [],
    obligation: '',
    commitment: 'Data export provided in portable, machine-readable format.',
    category: 'data_rights',
    type: 'conditional',
    sla: null,
    specifics: 'Users may request a complete export of their data. The export is provided in a portable, machine-readable format (e.g., JSON, CSV) that allows the user to transfer their data to another service.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§6', language: 'Data export in portable, machine-readable format.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Export function generates structured JSON/CSV; validated against schema before delivery',
      suspenders: 'Quarterly test export confirms readability and completeness',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['data export', 'portable', 'machine-readable', 'data portability'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy', '1.3-consumer-health-data-policy'],
  },

  {
    id: 'CMT-035',
    scopeActivities: ['S-009'],
    requirements: [],
    obligation: '',
    commitment: 'Data export provided within 10 business days.',
    category: 'sla_enforcement',
    type: 'conditional',
    sla: '10 business days',
    specifics: 'Data export requests are fulfilled within 10 business days of the request. The user receives an email notification when their export is ready for download.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§6', language: 'Data export provided within 10 business days of request.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Export queue with 10-business-day SLA timer; auto-escalation at day 7',
      suspenders: 'Weekly report of pending exports flags any approaching deadline',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['10 business days', 'data export', 'request fulfillment', 'export delivery'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  {
    id: 'CMT-036',
    scopeActivities: ['S-009'],
    requirements: [],
    obligation: '',
    commitment: 'Data export includes: account info, lab results, AI insights, consultation notes, order history, communications.',
    category: 'data_rights',
    type: 'conditional',
    sla: null,
    specifics: 'The data export package includes all user data categories: account information, uploaded lab results, AI-generated insights, telehealth consultation notes, supplement/product order history, and account-related communications.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§6', language: 'Export includes account info, lab results, AI insights, consultation notes, order history, and communications.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Export builder queries all 6 data categories; completeness check before delivery',
      suspenders: 'Test user export reviewed quarterly to confirm all categories present',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['account info', 'lab results', 'AI insights', 'consultation notes', 'order history'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  {
    id: 'CMT-037',
    scopeActivities: ['S-010', 'S-012'],
    requirements: [],
    obligation: '',
    commitment: 'Data retained after deletion ONLY for: legal obligations, fraud prevention, dispute resolution, de-identified data.',
    category: 'data_rights',
    type: 'conditional',
    sla: null,
    specifics: 'After account deletion, HemoLogix may retain limited data only for: (1) compliance with legal obligations, (2) fraud prevention, (3) pending dispute resolution, and (4) de-identified/aggregated data that cannot be re-identified. No other retention is permitted.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§6', language: 'Post-deletion retention only for legal obligations, fraud prevention, dispute resolution, and de-identified data.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Deletion pipeline tags retained records with legal basis; all other records permanently purged',
      suspenders: 'Quarterly audit of retained-after-deletion records confirms valid legal basis for each',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['post-deletion retention', 'legal obligations', 'fraud prevention', 'de-identified', 'limited retention'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy', '1.3-consumer-health-data-policy'],
  },

  {
    id: 'CMT-038',
    scopeActivities: ['S-010'],
    requirements: [],
    obligation: '',
    commitment: 'Deletion confirmation with cryptographic proof hash.',
    category: 'data_rights',
    type: 'conditional',
    sla: null,
    specifics: 'Upon completion of account deletion, the user receives a confirmation that includes a cryptographic proof hash verifying that their data has been permanently deleted from active systems.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§6', language: 'Deletion confirmation with proof hash provided upon completion.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Deletion pipeline generates SHA-256 hash of deletion manifest; included in confirmation email',
      suspenders: 'Hash verification tool allows user to confirm deletion was complete',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['deletion confirmation', 'proof hash', 'cryptographic', 'verification'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  {
    id: 'CMT-039',
    scopeActivities: ['S-009', 'S-010'],
    requirements: [],
    obligation: '',
    commitment: 'Terminated accounts (by HemoLogix) get 30-day data export window.',
    category: 'data_rights',
    type: 'conditional',
    sla: '30 calendar days',
    specifics: 'When HemoLogix terminates an account (except for fraud or serious TOS violation), the user is given a 30-day window to export their data before it is permanently deleted.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§6', language: 'Terminated accounts receive a 30-day window to export data.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Termination workflow grants 30-day export access; countdown timer visible to user',
      suspenders: 'Audit log confirms export window was provided for all non-fraud terminations',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['30-day export window', 'terminated accounts', 'data export', 'before deletion'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  // ════════════════════════════════════════════
  // SECTION 7: INTELLECTUAL PROPERTY
  // ════════════════════════════════════════════

  {
    id: 'CMT-040',
    scopeActivities: ['S-013'],
    requirements: [],
    obligation: '',
    commitment: 'Platform Technology owned by third-party IP Owner, licensed to HemoLogix (licensed-from model).',
    category: 'entity_integrity',
    type: 'ongoing',
    sla: null,
    specifics: 'The Platform Technology (software, algorithms, AI models, trade secrets) is owned by a third-party IP Owner (the trust/holding entity) and licensed to HemoLogix LLC under a separate IP License Agreement. HemoLogix does not own the core technology.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§7', language: 'Platform Technology is owned by IP Owner and licensed to HemoLogix.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'IP License Agreement on file; entity registry labels IP Owner correctly',
      suspenders: 'Annual IP audit confirms license is current and IP Owner entity is active',
    },
    priority: 'P0',
    owner: 'attorney',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['IP Owner', 'licensed-from', 'Platform Technology', 'IP License Agreement'],
    appearsInTemplates: ['1.1-terms-of-service', '3.2-ip-assignment-trust', '3.4-ip-license-agreement'],
  },

  {
    id: 'CMT-041',
    scopeActivities: ['S-002', 'S-013'],
    requirements: [],
    obligation: '',
    commitment: 'User retains ownership of all content they upload.',
    category: 'data_rights',
    type: 'ongoing',
    sla: null,
    specifics: 'Users retain full ownership of all content they upload to the Service, including lab results, health data, and any other user-generated content. HemoLogix receives only a limited license to process this content for service delivery.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§7', language: 'You retain ownership of all content you upload to the Service.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'TOS and privacy policy explicitly state user ownership; no IP assignment clauses',
      suspenders: 'Legal review confirms no provisions that could be interpreted as ownership transfer',
    },
    priority: 'P1',
    owner: 'attorney',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['user ownership', 'uploaded content', 'retains ownership', 'limited license'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  {
    id: 'CMT-042',
    scopeActivities: ['S-013'],
    requirements: [],
    obligation: '',
    commitment: 'IP Owner is express third-party beneficiary of IP provisions.',
    category: 'entity_integrity',
    type: 'ongoing',
    sla: null,
    specifics: 'The IP Owner (trust/holding entity) is an express third-party beneficiary of the intellectual property provisions in the TOS. This gives the IP Owner direct enforcement rights against users who infringe on the Platform Technology.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§7', language: 'IP Owner is an express third-party beneficiary of these intellectual property provisions.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'TOS contains express third-party beneficiary clause for IP Owner',
      suspenders: 'IP License Agreement cross-references TOS beneficiary status',
    },
    priority: 'P1',
    owner: 'attorney',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['third-party beneficiary', 'IP Owner', 'enforcement rights', 'IP provisions'],
    appearsInTemplates: ['1.1-terms-of-service', '3.2-ip-assignment-trust', '3.4-ip-license-agreement'],
  },

  {
    id: 'CMT-043',
    scopeActivities: ['S-013'],
    requirements: [],
    obligation: '',
    commitment: 'DMCA agent designated at dmca@hemologix.com.',
    category: 'regulatory_specific',
    type: 'ongoing',
    sla: null,
    specifics: 'HemoLogix has a designated DMCA agent reachable at dmca@hemologix.com for receiving copyright infringement notices under the Digital Millennium Copyright Act. The agent is registered with the U.S. Copyright Office.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§7', language: 'DMCA agent designated at dmca@hemologix.com.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'dmca@ email alias active and monitored; Copyright Office registration current',
      suspenders: 'Annual verification of DMCA agent registration with Copyright Office',
    },
    priority: 'P2',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['DMCA agent', 'dmca@hemologix.com', 'copyright', 'designated agent'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-044',
    scopeActivities: ['S-013'],
    requirements: [],
    obligation: '',
    commitment: 'DMCA counter-notification process available to users.',
    category: 'regulatory_specific',
    type: 'conditional',
    sla: null,
    specifics: 'Users who receive a DMCA takedown notice may submit a counter-notification through the process described in the TOS. HemoLogix will process counter-notifications in accordance with the DMCA safe harbor provisions.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§7', language: 'Counter-notification process available for DMCA takedown disputes.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Counter-notification form/process documented and accessible',
      suspenders: 'Legal review confirms counter-notification process meets DMCA requirements',
    },
    priority: 'P2',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['counter-notification', 'DMCA', 'takedown dispute', 'safe harbor'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  // ════════════════════════════════════════════
  // SECTION 8: PAYMENT, BILLING, AND REFUNDS
  // ════════════════════════════════════════════

  {
    id: 'CMT-045',
    scopeActivities: ['S-004', 'S-007'],
    requirements: [],
    obligation: '',
    commitment: 'Completed Tier 2 consultations are non-refundable.',
    category: 'financial',
    type: 'ongoing',
    sla: null,
    specifics: 'Once a Tier 2 telehealth consultation has been completed (i.e., the provider-patient interaction has occurred), the consultation fee is non-refundable.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§8', language: 'Completed Tier 2 consultations are non-refundable.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Refund system blocks refund requests for consultations marked as completed',
      suspenders: 'Monthly refund audit confirms no refunds issued for completed consultations',
    },
    priority: 'P1',
    owner: 'system',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['completed consultation', 'non-refundable', 'Tier 2', 'no refund'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-046',
    scopeActivities: ['S-004', 'S-007'],
    requirements: [],
    obligation: '',
    commitment: 'Tier 2 cancellation 24+ hours before appointment receives full refund.',
    category: 'financial',
    type: 'conditional',
    sla: null,
    specifics: 'If a user cancels a scheduled Tier 2 consultation at least 24 hours before the appointment time, they receive a full refund of the consultation fee.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§8', language: 'Cancellation 24+ hours before consultation: full refund.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Cancellation system auto-calculates hours before appointment; triggers full refund if 24+',
      suspenders: 'Refund audit confirms all 24+ hour cancellations received full refund',
    },
    priority: 'P1',
    owner: 'system',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['24-hour cancellation', 'full refund', 'Tier 2', 'before appointment'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-047',
    scopeActivities: ['S-004', 'S-007'],
    requirements: [],
    obligation: '',
    commitment: 'Tier 2 cancellation less than 24 hours before is non-refundable.',
    category: 'financial',
    type: 'conditional',
    sla: null,
    specifics: 'If a user cancels a scheduled Tier 2 consultation less than 24 hours before the appointment time, the consultation fee is non-refundable.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§8', language: 'Cancellation less than 24 hours before consultation: non-refundable.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Cancellation system blocks refund for sub-24-hour cancellations; user warned at time of cancel',
      suspenders: 'Refund audit confirms no refunds issued for late cancellations',
    },
    priority: 'P1',
    owner: 'system',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['late cancellation', 'non-refundable', 'less than 24 hours', 'Tier 2'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-048',
    scopeActivities: ['S-004', 'S-007'],
    requirements: [],
    obligation: '',
    commitment: 'Provider cancellation results in full refund.',
    category: 'financial',
    type: 'conditional',
    sla: null,
    specifics: 'If a provider cancels a scheduled Tier 2 consultation for any reason, the user receives a full refund of the consultation fee automatically.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§8', language: 'Provider cancellation: full refund to user.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Provider cancellation event triggers automatic full refund via Stripe',
      suspenders: 'Monthly audit confirms all provider cancellations resulted in full refund',
    },
    priority: 'P1',
    owner: 'system',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['provider cancellation', 'full refund', 'automatic refund', 'Tier 2'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-049',
    scopeActivities: ['S-006', 'S-007'],
    requirements: [],
    obligation: '',
    commitment: 'Tier 3 unopened products eligible for 30-day return with full refund.',
    category: 'financial',
    type: 'conditional',
    sla: '30 calendar days',
    specifics: 'Unopened Tier 3 supplement products may be returned within 30 days of delivery for a full refund. Products must be in original, sealed packaging.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§8', language: 'Unopened products: 30-day return for full refund.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Return system calculates days from delivery date; accepts returns within 30 days for unopened',
      suspenders: 'Monthly return audit confirms refund policy applied correctly',
    },
    priority: 'P1',
    owner: 'system',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['30-day return', 'unopened', 'full refund', 'Tier 3', 'supplements'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-050',
    scopeActivities: ['S-006', 'S-007'],
    requirements: [],
    obligation: '',
    commitment: 'Tier 3 opened products are non-refundable except if defective or wrong item.',
    category: 'financial',
    type: 'conditional',
    sla: null,
    specifics: 'Opened Tier 3 supplement products are non-refundable. Exception: if the product is defective or the wrong item was shipped, a refund or replacement is provided.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§8', language: 'Opened products non-refundable except defective or wrong item.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Return system requires defect/wrong-item justification for opened products',
      suspenders: 'Refund audit confirms opened-product refunds only for valid exceptions',
    },
    priority: 'P1',
    owner: 'system',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['opened products', 'non-refundable', 'defective', 'wrong item', 'exception'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-051',
    scopeActivities: ['S-006', 'S-007'],
    requirements: [],
    obligation: '',
    commitment: 'Defective product claim must be filed within 7 days with photographic evidence.',
    category: 'financial',
    type: 'conditional',
    sla: '7 calendar days',
    specifics: 'Users must report defective products within 7 days of delivery and provide photographic evidence of the defect. Claims outside this window or without evidence may be denied.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§8', language: 'Defective product claims within 7 days with photographic evidence.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Defect claim form requires photo upload and validates delivery date within 7 days',
      suspenders: 'Claims review queue ensures all defect claims meet evidence requirements',
    },
    priority: 'P2',
    owner: 'admin',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['7-day claim', 'defective product', 'photographic evidence', 'defect report'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-052',
    scopeActivities: ['S-007'],
    requirements: [],
    obligation: '',
    commitment: 'Refunds processed within 10 business days.',
    category: 'sla_enforcement',
    type: 'conditional',
    sla: '10 business days',
    specifics: 'All approved refunds are processed and returned to the original payment method within 10 business days of approval.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§8', language: 'Refunds processed within 10 business days.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Refund queue with 10-business-day SLA timer; auto-escalation at day 7',
      suspenders: 'Weekly refund status report flags any approaching SLA deadline',
    },
    priority: 'P1',
    owner: 'system',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['10 business days', 'refund processing', 'original payment method', 'refund SLA'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-053',
    scopeActivities: ['S-005', 'S-007'],
    requirements: [],
    obligation: '',
    commitment: 'Prescription medications non-refundable once dispensed.',
    category: 'financial',
    type: 'ongoing',
    sla: null,
    specifics: 'Once prescription medications have been dispensed by the pharmacy, they are non-refundable. This is standard practice due to pharmacy regulations and safety concerns.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§8', language: 'Prescription medications are non-refundable once dispensed.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Refund system blocks refund requests for dispensed prescriptions',
      suspenders: 'Monthly audit confirms no refunds issued for dispensed medications',
    },
    priority: 'P1',
    owner: 'system',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['prescription medications', 'non-refundable', 'once dispensed', 'pharmacy'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-054',
    scopeActivities: ['S-007'],
    requirements: [],
    obligation: '',
    commitment: 'Billing disputes must be filed within 30 days; response within 15 business days.',
    category: 'sla_enforcement',
    type: 'conditional',
    sla: '15 business days',
    specifics: 'Users must raise billing disputes within 30 days of the charge. HemoLogix will respond to billing disputes within 15 business days of receipt.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§8', language: 'Billing disputes within 30 days; response within 15 business days.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Billing dispute queue with 15-business-day response SLA; auto-escalation at day 12',
      suspenders: 'Weekly dispute status report flags any approaching deadline',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['billing dispute', '30-day filing', '15 business days', 'dispute response'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  // ════════════════════════════════════════════
  // SECTION 9: LIMITATION OF LIABILITY
  // ════════════════════════════════════════════

  {
    id: 'CMT-055',
    scopeActivities: ['S-001', 'S-002', 'S-003', 'S-004', 'S-005', 'S-006'],
    requirements: [],
    obligation: '',
    commitment: 'Service provided "as is" with no warranties.',
    category: 'entity_integrity',
    type: 'ongoing',
    sla: null,
    specifics: 'The Service is provided on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind, whether express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§9', language: 'THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'TOS contains "as is" disclaimer in required capitalized format',
      suspenders: 'Legal review confirms no other documents create implied warranties',
    },
    priority: 'P1',
    owner: 'attorney',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['as is', 'no warranties', 'as available', 'disclaimer'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-056',
    scopeActivities: ['S-004', 'S-005'],
    requirements: [],
    obligation: '',
    commitment: 'Clinical liability rests with treating provider and OpenLoop, not HemoLogix.',
    category: 'entity_integrity',
    type: 'ongoing',
    sla: null,
    specifics: 'Any liability arising from clinical decisions, medical advice, prescriptions, or treatment outcomes rests with the treating provider and OpenLoop, not with HemoLogix. HemoLogix is a technology platform facilitator only.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§9', language: 'Clinical liability rests with the treating provider and OpenLoop, not HemoLogix.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'MSA with OpenLoop includes clinical liability allocation; TOS disclaims clinical liability',
      suspenders: 'Insurance coverage review confirms HemoLogix not covering clinical malpractice',
    },
    priority: 'P0',
    owner: 'attorney',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['clinical liability', 'treating provider', 'OpenLoop', 'not HemoLogix', 'CPOM'],
    appearsInTemplates: ['1.1-terms-of-service', '1.7-telehealth-informed-consent', '4.1-msa-llc-pc'],
  },

  {
    id: 'CMT-057',
    scopeActivities: ['S-014'],
    requirements: [],
    obligation: '',
    commitment: 'Aggregate liability capped at greater of $100 or 12 months of fees paid.',
    category: 'financial',
    type: 'ongoing',
    sla: null,
    specifics: 'HemoLogix\'s total aggregate liability for all claims arising out of or relating to the Service is limited to the greater of $100 or the total fees paid by the user in the 12 months preceding the claim.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§9', language: 'Aggregate liability capped at the greater of $100 or fees paid in the preceding 12 months.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Legal dispute tracking system calculates liability cap per user based on payment history',
      suspenders: 'Attorney review of any settlement ensures cap is respected',
    },
    priority: 'P1',
    owner: 'attorney',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['$100', '12 months', 'aggregate liability', 'liability cap', 'fees paid'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  // ════════════════════════════════════════════
  // SECTION 10: DISPUTE RESOLUTION
  // ════════════════════════════════════════════

  {
    id: 'CMT-058',
    scopeActivities: ['S-014'],
    requirements: [],
    obligation: '',
    commitment: '30-day informal resolution period required before arbitration.',
    category: 'sla_enforcement',
    type: 'conditional',
    sla: '30 calendar days',
    specifics: 'Before initiating binding arbitration, the parties must attempt to resolve the dispute informally for a period of 30 days. This informal resolution period begins when one party sends written notice of the dispute to the other.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§10', language: '30-day informal resolution period before arbitration may be initiated.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Dispute tracking system enforces 30-day waiting period before arbitration filing',
      suspenders: 'Legal team confirms informal resolution attempt documented for every dispute',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['30-day informal', 'resolution period', 'before arbitration', 'written notice'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-059',
    scopeActivities: ['S-014'],
    requirements: [],
    obligation: '',
    commitment: 'Good-faith negotiation including at least one telephone or video call.',
    category: 'sla_enforcement',
    type: 'conditional',
    sla: null,
    specifics: 'During the 30-day informal resolution period, the parties must engage in good-faith negotiation, which must include at least one telephone or video conference call to discuss the dispute.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§10', language: 'Good-faith negotiation including at least one call.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Dispute resolution log requires call date/time entry before arbitration can proceed',
      suspenders: 'Attorney confirms call occurred and was documented',
    },
    priority: 'P2',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['good-faith negotiation', 'telephone call', 'video call', 'informal resolution'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-060',
    scopeActivities: ['S-014'],
    requirements: [],
    obligation: '',
    commitment: 'Binding arbitration via AAA in Miami-Dade County, FL.',
    category: 'regulatory_specific',
    type: 'conditional',
    sla: null,
    specifics: 'If informal resolution fails, disputes are resolved through binding arbitration administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules, conducted in Miami-Dade County, Florida.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§10', language: 'Binding arbitration via AAA in Miami-Dade County, Florida.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'All dispute filings routed through AAA; venue set to Miami-Dade County',
      suspenders: 'Attorney review confirms AAA jurisdiction and venue for each dispute',
    },
    priority: 'P1',
    owner: 'attorney',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['AAA', 'binding arbitration', 'Miami-Dade County', 'Consumer Arbitration Rules'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-061',
    scopeActivities: ['S-014'],
    requirements: [],
    obligation: '',
    commitment: 'Class action waiver — all disputes on individual basis only.',
    category: 'regulatory_specific',
    type: 'ongoing',
    sla: null,
    specifics: 'Users waive the right to participate in class actions, class arbitrations, or representative proceedings. All disputes must be resolved on an individual basis.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§10', language: 'CLASS ACTION WAIVER: All disputes resolved on individual basis only.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'TOS contains class action waiver in required capitalized format',
      suspenders: 'Attorney confirms waiver enforceability in current jurisdiction',
    },
    priority: 'P1',
    owner: 'attorney',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['class action waiver', 'individual basis', 'no class arbitration', 'representative proceedings'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-062',
    scopeActivities: ['S-014'],
    requirements: [],
    obligation: '',
    commitment: '30-day opt-out window for arbitration agreement.',
    category: 'consent_management',
    type: 'conditional',
    sla: '30 calendar days',
    specifics: 'New users have 30 days from account creation to opt out of the binding arbitration agreement by sending written notice. Users who opt out retain access to the Service but may pursue disputes in court.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§10', language: '30-day opt-out window for arbitration agreement from date of account creation.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'System tracks arbitration opt-out status per user; 30-day window from registration date',
      suspenders: 'Opt-out requests logged and confirmed via email acknowledgment',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['30-day opt-out', 'arbitration', 'written notice', 'opt-out window'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-063',
    scopeActivities: ['S-007', 'S-014'],
    requirements: [],
    obligation: '',
    commitment: 'HemoLogix pays arbitration fees for claims under $10,000.',
    category: 'financial',
    type: 'conditional',
    sla: null,
    specifics: 'For claims where the amount in controversy is less than $10,000, HemoLogix will pay all arbitration filing fees, administration fees, and arbitrator fees.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§10', language: 'HemoLogix pays arbitration fees for claims under $10,000.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Dispute tracking flags claims under $10,000 for HemoLogix fee payment',
      suspenders: 'Finance team confirms arbitration fee payments for qualifying claims',
    },
    priority: 'P2',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['arbitration fees', 'under $10,000', 'HemoLogix pays', 'fee payment'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-064',
    scopeActivities: ['S-014'],
    requirements: [],
    obligation: '',
    commitment: '1-year statute of limitations on all claims.',
    category: 'regulatory_specific',
    type: 'ongoing',
    sla: '1 year',
    specifics: 'Any claim arising out of or relating to the Service must be filed within one year after the cause of action accrues. Claims filed after the 1-year period are permanently barred.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§10', language: 'Claims must be filed within one year after the cause of action accrues.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Dispute tracking system calculates 1-year window from event date',
      suspenders: 'Attorney raises statute of limitations defense for any out-of-window claims',
    },
    priority: 'P1',
    owner: 'attorney',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['1-year limitation', 'statute of limitations', 'permanently barred', 'cause of action'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  // ════════════════════════════════════════════
  // SECTION 11: TERMINATION
  // ════════════════════════════════════════════

  {
    id: 'CMT-065',
    scopeActivities: ['S-001'],
    requirements: [],
    obligation: '',
    commitment: 'HemoLogix may terminate for: TOS violation, fraud, 12-month inactivity, legal requirement.',
    category: 'entity_integrity',
    type: 'conditional',
    sla: null,
    specifics: 'HemoLogix reserves the right to terminate user accounts for: (1) violation of TOS, (2) fraudulent activity, (3) 12 months of account inactivity, or (4) legal or regulatory requirement. These are the only permissible termination grounds.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§11', language: 'HemoLogix may terminate for TOS violation, fraud, 12-month inactivity, or legal requirement.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Termination system requires reason code from enumerated list; no ad-hoc terminations',
      suspenders: 'Quarterly audit of terminations confirms all have valid reason codes',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['termination grounds', 'TOS violation', 'fraud', '12-month inactivity', 'legal requirement'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-066',
    scopeActivities: ['S-001', 'S-008'],
    requirements: [],
    obligation: '',
    commitment: 'Inactivity termination requires prior notice and reactivation opportunity.',
    category: 'notification',
    type: 'conditional',
    sla: null,
    specifics: 'Before terminating an account for 12-month inactivity, HemoLogix must send prior notice to the user and provide an opportunity to reactivate the account. The notice must be sent to the registered email address.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§11', language: 'Prior notice and reactivation opportunity before inactivity termination.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Inactivity monitor sends warning email at 11 months; termination only proceeds if no activity after notice',
      suspenders: 'Termination audit confirms notice was sent before every inactivity termination',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['inactivity notice', 'prior notice', 'reactivation opportunity', '12-month inactivity'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-067',
    scopeActivities: ['S-009', 'S-010'],
    requirements: [],
    obligation: '',
    commitment: 'Termination by HemoLogix triggers 30-day data export window (unless fraud/violation).',
    category: 'data_rights',
    type: 'conditional',
    sla: '30 calendar days',
    specifics: 'When HemoLogix terminates an account (for inactivity or legal requirement), the user receives a 30-day window to export their data. This window does NOT apply to terminations for fraud or serious TOS violations.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§11', language: '30-day data export window upon termination by HemoLogix, except for fraud or violation.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Termination workflow checks reason code; non-fraud terminations auto-grant 30-day export window',
      suspenders: 'Audit log confirms export window provided for all eligible terminations',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['30-day export window', 'termination', 'data export', 'except fraud'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  {
    id: 'CMT-068',
    scopeActivities: ['S-014'],
    requirements: [],
    obligation: '',
    commitment: 'Surviving provisions: IP (§7), Payment (§8), Liability (§9), Arbitration (§10), General (§13).',
    category: 'entity_integrity',
    type: 'ongoing',
    sla: null,
    specifics: 'Upon termination of the TOS (by either party), the following sections survive: Intellectual Property (§7), Payment/Billing/Refunds (§8), Limitation of Liability (§9), Dispute Resolution (§10), and General Provisions (§13).',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§11', language: 'Sections 7, 8, 9, 10, and 13 survive termination.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'TOS contains explicit survival clause listing surviving sections',
      suspenders: 'Attorney review confirms surviving provisions are enforceable post-termination',
    },
    priority: 'P1',
    owner: 'attorney',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['surviving provisions', 'post-termination', '§7 §8 §9 §10 §13', 'survival clause'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  // ════════════════════════════════════════════
  // SECTION 12: PRIVACY AND REGULATORY
  // ════════════════════════════════════════════

  {
    id: 'CMT-069',
    scopeActivities: ['S-012'],
    requirements: [],
    obligation: '',
    commitment: 'Privacy Policy published at hemologix.com/privacy.',
    category: 'compliance_documentation',
    type: 'ongoing',
    sla: null,
    specifics: 'The Privacy Policy is publicly available at hemologix.com/privacy. It describes data collection, use, sharing, and user rights. It must be accessible without requiring login.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§12', language: 'Privacy Policy available at hemologix.com/privacy.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'URL monitoring confirms hemologix.com/privacy returns 200 status',
      suspenders: 'Monthly content check confirms Privacy Policy is current version',
    },
    priority: 'P0',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['Privacy Policy', 'hemologix.com/privacy', 'publicly available', 'data practices'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  {
    id: 'CMT-070',
    scopeActivities: ['S-012'],
    requirements: [],
    obligation: '',
    commitment: 'Consumer Health Data Policy at hemologix.com/consumer-health-data (MHMDA compliance).',
    category: 'compliance_documentation',
    type: 'ongoing',
    sla: null,
    specifics: 'The Consumer Health Data Policy is published at hemologix.com/consumer-health-data in compliance with the My Health My Data Act (MHMDA) and similar state consumer health data laws.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§12', language: 'Consumer Health Data Policy at hemologix.com/consumer-health-data.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'URL monitoring confirms hemologix.com/consumer-health-data returns 200 status',
      suspenders: 'Attorney review confirms MHMDA compliance of published policy',
    },
    priority: 'P0',
    owner: 'attorney',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['Consumer Health Data', 'MHMDA', 'hemologix.com/consumer-health-data', 'consumer health'],
    appearsInTemplates: ['1.1-terms-of-service', '1.3-consumer-health-data-policy'],
  },

  {
    id: 'CMT-071',
    scopeActivities: ['S-004', 'S-012'],
    requirements: [],
    obligation: '',
    commitment: 'HIPAA Notice of Privacy Practices at hemologix.com/hipaa-notice (Tier 2 users).',
    category: 'compliance_documentation',
    type: 'ongoing',
    sla: null,
    specifics: 'The HIPAA Notice of Privacy Practices (NPP) is published at hemologix.com/hipaa-notice. It applies to Tier 2 users whose health data becomes PHI through the telehealth consultation relationship.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§12', language: 'HIPAA NPP at hemologix.com/hipaa-notice for Tier 2 users.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'URL monitoring confirms hemologix.com/hipaa-notice returns 200 status',
      suspenders: 'HIPAA compliance officer reviews NPP annually',
    },
    priority: 'P0',
    owner: 'attorney',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['HIPAA', 'Notice of Privacy Practices', 'hemologix.com/hipaa-notice', 'Tier 2', 'PHI'],
    appearsInTemplates: ['1.1-terms-of-service', '1.4-hipaa-npp', '5.1-baa-llc-pc'],
  },

  {
    id: 'CMT-072',
    scopeActivities: ['S-004', 'S-012'],
    requirements: [],
    obligation: '',
    commitment: 'Telehealth Informed Consent at hemologix.com/telehealth-consent (Tier 2 users).',
    category: 'compliance_documentation',
    type: 'ongoing',
    sla: null,
    specifics: 'The Telehealth Informed Consent document is published at hemologix.com/telehealth-consent. It must be reviewed and signed by Tier 2 users before their first consultation.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§12', language: 'Telehealth Informed Consent at hemologix.com/telehealth-consent.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'URL monitoring confirms hemologix.com/telehealth-consent returns 200 status',
      suspenders: 'Consent records confirm all Tier 2 users signed before first consultation',
    },
    priority: 'P0',
    owner: 'attorney',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['Telehealth Informed Consent', 'hemologix.com/telehealth-consent', 'Tier 2', 'before consultation'],
    appearsInTemplates: ['1.1-terms-of-service', '1.7-telehealth-informed-consent'],
  },

  {
    id: 'CMT-073',
    scopeActivities: ['S-012'],
    requirements: [],
    obligation: '',
    commitment: 'Cookie Policy at hemologix.com/cookies.',
    category: 'compliance_documentation',
    type: 'ongoing',
    sla: null,
    specifics: 'The Cookie Policy is published at hemologix.com/cookies. It describes cookie usage, types of cookies, third-party cookies, and user controls for cookie preferences.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§12', language: 'Cookie Policy at hemologix.com/cookies.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'URL monitoring confirms hemologix.com/cookies returns 200 status',
      suspenders: 'Quarterly review confirms Cookie Policy matches actual cookie usage',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['Cookie Policy', 'hemologix.com/cookies', 'cookie preferences', 'tracking'],
    appearsInTemplates: ['1.1-terms-of-service', '1.5-cookie-policy'],
  },

  {
    id: 'CMT-074',
    scopeActivities: ['S-012'],
    requirements: [],
    obligation: '',
    commitment: 'CCPA/CPRA rights: know, delete, opt-out, correct, limit, non-discrimination, portability.',
    category: 'regulatory_specific',
    type: 'ongoing',
    sla: null,
    specifics: 'California users have the following rights under CCPA/CPRA: (1) right to know what data is collected, (2) right to delete, (3) right to opt out of sale/sharing, (4) right to correct, (5) right to limit use of sensitive data, (6) right to non-discrimination, (7) right to data portability.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§12', language: 'CCPA/CPRA rights: know, delete, opt-out, correct, limit, non-discrimination, portability.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Privacy rights request system supports all 7 CCPA/CPRA right types',
      suspenders: 'Annual CCPA compliance audit confirms all rights are operationally supported',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['CCPA', 'CPRA', 'right to know', 'right to delete', 'opt-out', 'non-discrimination'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy', '1.3-consumer-health-data-policy'],
  },

  {
    id: 'CMT-075',
    scopeActivities: ['S-001', 'S-012'],
    requirements: [],
    obligation: '',
    commitment: 'Service intended for US users only — international users at own risk.',
    category: 'regulatory_specific',
    type: 'ongoing',
    sla: null,
    specifics: 'The Service is intended for use within the United States. International users may access the Service but do so at their own risk and are responsible for compliance with their local laws. HemoLogix makes no representation of compliance with non-US laws.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§12', language: 'Service intended for US users only; international users at own risk.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Registration flow displays US-only notice; geolocation advisory for non-US IP addresses',
      suspenders: 'Terms clearly disclaim international legal compliance',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['US only', 'international users', 'own risk', 'United States'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  {
    id: 'CMT-076',
    scopeActivities: ['S-001', 'S-012'],
    requirements: [],
    obligation: '',
    commitment: 'No knowingly collecting data from EEA/UK users.',
    category: 'regulatory_specific',
    type: 'prohibitive',
    sla: null,
    specifics: 'HemoLogix does not knowingly collect personal data from users located in the European Economic Area (EEA) or United Kingdom (UK). If EEA/UK users are detected, appropriate measures are taken.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§12', language: 'We do not knowingly collect data from EEA/UK users.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Geolocation check flags EEA/UK IP addresses during registration with advisory notice',
      suspenders: 'Quarterly audit of user base for EEA/UK indicators',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['EEA', 'UK', 'not knowingly collecting', 'GDPR', 'geolocation'],
    appearsInTemplates: ['1.1-terms-of-service', '1.2-privacy-policy'],
  },

  // ════════════════════════════════════════════
  // SECTION 13: GENERAL PROVISIONS
  // ════════════════════════════════════════════

  {
    id: 'CMT-077',
    scopeActivities: ['S-014'],
    requirements: [],
    obligation: '',
    commitment: 'Assignment allowed on merger, acquisition, or sale of substantially all assets.',
    category: 'entity_integrity',
    type: 'conditional',
    sla: null,
    specifics: 'HemoLogix may assign the TOS and all rights/obligations thereunder in connection with a merger, acquisition, or sale of substantially all of its assets. Users will be notified of any such assignment.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§13', language: 'Assignment permitted in connection with merger, acquisition, or sale of substantially all assets.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Operating Agreement includes assignment provisions consistent with TOS',
      suspenders: 'Attorney review required before any assignment event',
    },
    priority: 'P2',
    owner: 'attorney',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['assignment', 'merger', 'acquisition', 'substantially all assets'],
    appearsInTemplates: ['1.1-terms-of-service', '2.1-operating-agreement'],
  },

  {
    id: 'CMT-078',
    scopeActivities: ['S-014'],
    requirements: [],
    obligation: '',
    commitment: 'Force majeure clause — not liable for events beyond reasonable control.',
    category: 'entity_integrity',
    type: 'conditional',
    sla: null,
    specifics: 'HemoLogix is not liable for failure to perform obligations due to events beyond its reasonable control, including natural disasters, war, terrorism, pandemics, government actions, internet outages, and similar force majeure events.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§13', language: 'Not liable for events beyond reasonable control (force majeure).' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'TOS contains force majeure clause with enumerated events',
      suspenders: 'Attorney confirms force majeure clause is enforceable and comprehensive',
    },
    priority: 'P2',
    owner: 'attorney',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['force majeure', 'beyond reasonable control', 'natural disaster', 'not liable'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-079',
    scopeActivities: ['S-008'],
    requirements: [],
    obligation: '',
    commitment: 'Notices to users via email — deemed delivered 24 hours after sent.',
    category: 'notification',
    type: 'ongoing',
    sla: '24 hours',
    specifics: 'Notices from HemoLogix to users are sent via email to the registered email address. Notices are deemed delivered 24 hours after being sent, regardless of whether the email is actually opened or read.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§13', language: 'Notices via email deemed delivered 24 hours after sending.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Email delivery system logs send timestamp; 24-hour deemed-delivery timer starts automatically',
      suspenders: 'Email bounce monitoring ensures notices reach valid addresses',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['email notice', 'deemed delivered', '24 hours', 'registered email'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  {
    id: 'CMT-080',
    scopeActivities: ['S-012'],
    requirements: [],
    obligation: '',
    commitment: 'Respond to user inquiries within 10 business days.',
    category: 'sla_enforcement',
    type: 'ongoing',
    sla: '10 business days',
    specifics: 'HemoLogix will respond to user inquiries (support requests, privacy requests, general questions) within 10 business days of receipt.',
    documents: [
      { templateId: '1.1-terms-of-service', section: '§13', language: 'Respond to inquiries within 10 business days.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: [],
    verification: {
      belt: 'Support ticket system enforces 10-business-day SLA with auto-escalation at day 7',
      suspenders: 'Weekly SLA compliance report flags overdue inquiries',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['10 business days', 'inquiry response', 'support request', 'response SLA'],
    appearsInTemplates: ['1.1-terms-of-service'],
  },

  // ════════════════════════════════════════════
  // SOC 2 / INFORMATION SECURITY COMMITMENTS
  // ════════════════════════════════════════════

  // ── 13.1 Information Security Policy ──

  {
    id: 'CMT-081',
    scopeActivities: ['S-015'],
    requirements: ['REQ-058', 'REQ-059'],
    obligation: '',
    commitment: 'Annual review and update of Information Security Policy.',
    category: 'compliance_documentation',
    type: 'periodic',
    sla: 'Annually',
    specifics: 'The master Information Security Policy must be reviewed and updated annually. Review must cover all five SOC 2 Trust Service Criteria, verify subordinate policy alignment, and document any changes.',
    documents: [
      { templateId: '13.1-information-security-policy', section: '§10', language: 'Policy reviewed and updated annually by CEO.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['No automated policy review reminder system'],
    verification: {
      belt: 'Calendar reminder triggers annual review; updated policy version logged',
      suspenders: 'SOC 2 evidence map tracks policy review dates; auditor verifies currency',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['annual review', 'policy update', 'trust service criteria', 'security governance'],
    appearsInTemplates: ['13.1-information-security-policy', '13.10-soc2-evidence-map'],
  },
  {
    id: 'CMT-082',
    scopeActivities: ['S-015'],
    requirements: ['REQ-058'],
    obligation: '',
    commitment: 'Platform controls inherited by all brands, never overridden.',
    category: 'entity_integrity',
    type: 'ongoing',
    sla: null,
    specifics: 'Platform-level security controls (audit logging, access control, encryption, quality gates) are inherited by all brands and cannot be disabled or overridden at the brand layer.',
    documents: [
      { templateId: '13.1-information-security-policy', section: '§5', language: 'Platform controls are inherited, never overridden.' },
    ],
    systemId: null,
    systemStatus: 'partial',
    systemGaps: ['Multi-brand architecture not yet implemented'],
    verification: {
      belt: 'Firestore rules enforce platform controls at infrastructure level',
      suspenders: 'Brand launch checklist verifies all platform controls active before go-live',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['platform controls', 'inherited', 'never overridden', 'brand isolation'],
    appearsInTemplates: ['13.1-information-security-policy'],
  },

  // ── 13.2 Access Control Policy ──

  {
    id: 'CMT-083',
    scopeActivities: ['S-015', 'S-019'],
    requirements: ['REQ-060'],
    obligation: '',
    commitment: 'Quarterly access reviews conducted and documented.',
    category: 'compliance_documentation',
    type: 'periodic',
    sla: 'Quarterly',
    specifics: 'Every quarter, an admin reviews all user accounts by role and last login date. Stale accounts are flagged for deactivation. Review is documented with reviewer name, date, findings, and actions taken.',
    documents: [
      { templateId: '13.2-access-control-policy', section: '§6', language: 'Quarterly access review process with documented findings.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['Admin access review page not built yet'],
    verification: {
      belt: 'Admin dashboard lists all users by role with last login; quarterly review logged',
      suspenders: 'SOC 2 evidence map tracks review dates; auditor verifies documentation',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['quarterly access review', 'role review', 'last login', 'documented findings'],
    appearsInTemplates: ['13.2-access-control-policy', '13.10-soc2-evidence-map'],
  },
  {
    id: 'CMT-084',
    scopeActivities: ['S-015'],
    requirements: ['REQ-060'],
    obligation: '',
    commitment: 'MFA required for admin, provider, and owner roles.',
    category: 'safety',
    type: 'ongoing',
    sla: null,
    specifics: 'Multi-factor authentication is enforced for all non-patient roles. Firebase Auth native MFA. Optional for patients.',
    documents: [
      { templateId: '13.2-access-control-policy', section: '§4', language: 'MFA required for admin, provider, and owner roles.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['MFA not yet enabled in Firebase Auth config'],
    verification: {
      belt: 'Firebase Auth enforces MFA enrollment for privileged roles',
      suspenders: 'Quarterly access review verifies MFA status for all admin/provider/owner accounts',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['multi-factor authentication', 'MFA', 'admin', 'provider', 'owner'],
    appearsInTemplates: ['13.2-access-control-policy'],
  },
  {
    id: 'CMT-085',
    scopeActivities: ['S-015', 'S-019'],
    requirements: ['REQ-060'],
    obligation: '',
    commitment: 'Access revocation within 24 hours of offboarding.',
    category: 'sla_enforcement',
    type: 'conditional',
    sla: '24 hours',
    specifics: 'When an employee, contractor, or vendor is offboarded, all system access (Firebase, GitHub, admin dashboard, email, Slack) is revoked within 24 hours. Revocation logged to audit trail.',
    documents: [
      { templateId: '13.2-access-control-policy', section: '§7', language: 'Access revocation within 24 hours of offboarding.' },
      { templateId: '7.8-termination-checklist', section: '§1', language: 'Revoke all system access.' },
    ],
    systemId: null,
    systemStatus: 'partial',
    systemGaps: ['No automated offboarding workflow'],
    verification: {
      belt: 'Termination checklist triggers access revocation; audit log confirms timestamp',
      suspenders: 'Quarterly access review catches any orphaned accounts',
    },
    priority: 'P0',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['24 hours', 'access revocation', 'offboarding', 'termination'],
    appearsInTemplates: ['13.2-access-control-policy', '7.8-termination-checklist'],
  },
  {
    id: 'CMT-086',
    scopeActivities: ['S-015'],
    requirements: ['REQ-060'],
    obligation: '',
    commitment: 'Brand-scoped access isolation enforced via Firestore rules.',
    category: 'entity_integrity',
    type: 'ongoing',
    sla: null,
    specifics: 'Firestore security rules enforce brand-level data isolation. A patient in Brand A cannot access data from Brand B. Admin access is scoped to assigned brands only. Owner role spans all brands.',
    documents: [
      { templateId: '13.2-access-control-policy', section: '§8', language: 'Brand-scoped access isolation via Firestore rules.' },
    ],
    systemId: null,
    systemStatus: 'partial',
    systemGaps: ['Multi-brand Firestore rules not yet implemented'],
    verification: {
      belt: 'Firestore rules test suite verifies brand isolation on every deploy',
      suspenders: 'Penetration test validates cross-brand access prevention',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['brand isolation', 'Firestore rules', 'access control', 'data separation'],
    appearsInTemplates: ['13.2-access-control-policy'],
  },

  // ── 13.3 Change Management Policy ──

  {
    id: 'CMT-087',
    scopeActivities: ['S-016'],
    requirements: ['REQ-062'],
    obligation: '',
    commitment: 'All code changes via pull request with CI checks.',
    category: 'compliance_documentation',
    type: 'ongoing',
    sla: null,
    specifics: 'Every code change must go through a pull request. CI pipeline (TypeScript compile + ESLint + Vitest) must pass before merge. Direct commits to main branch are prohibited.',
    documents: [
      { templateId: '13.3-change-management-policy', section: '§3', language: 'Branch per feature/fix. Never commit directly to main.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['GitHub Actions CI pipeline not yet configured'],
    verification: {
      belt: 'GitHub branch protection rules block direct main commits; CI required to pass',
      suspenders: 'Deploy log shows commit SHA traceable to merged PR',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['pull request', 'CI', 'branch protection', 'code review'],
    appearsInTemplates: ['13.3-change-management-policy'],
  },
  {
    id: 'CMT-088',
    scopeActivities: ['S-016'],
    requirements: ['REQ-062'],
    obligation: '',
    commitment: 'Staging verification before production deployment.',
    category: 'compliance_documentation',
    type: 'ongoing',
    sla: null,
    specifics: 'All normal and major changes must be deployed to staging environment and verified before production promotion. Emergency changes may skip staging with post-deploy review within 24 hours.',
    documents: [
      { templateId: '13.3-change-management-policy', section: '§5', language: 'Staging verification required before production.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['hemologix-staging Firebase project not yet created'],
    verification: {
      belt: 'Deploy pipeline requires staging deploy before production promote',
      suspenders: 'Deploy log shows staging deploy timestamp before production deploy',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['staging', 'verification', 'production', 'deployment'],
    appearsInTemplates: ['13.3-change-management-policy'],
  },
  {
    id: 'CMT-089',
    scopeActivities: ['S-016'],
    requirements: ['REQ-062'],
    obligation: '',
    commitment: 'Emergency changes documented within 24 hours.',
    category: 'compliance_documentation',
    type: 'conditional',
    sla: '24 hours',
    specifics: 'Emergency changes that skip normal approval/staging can be deployed immediately but require post-deploy documentation within 24 hours covering what changed, why it was urgent, and what testing was done.',
    documents: [
      { templateId: '13.3-change-management-policy', section: '§10', language: 'Emergency changes documented within 24 hours.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['No emergency change documentation workflow'],
    verification: {
      belt: 'Emergency deploy creates audit log entry; 24-hour timer triggers documentation reminder',
      suspenders: 'Monthly change review identifies undocumented emergency changes',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['emergency change', '24 hours', 'post-deploy review', 'documentation'],
    appearsInTemplates: ['13.3-change-management-policy'],
  },
  {
    id: 'CMT-090',
    scopeActivities: ['S-016'],
    requirements: ['REQ-063'],
    obligation: '',
    commitment: 'Every production deploy logged with who/when/what.',
    category: 'compliance_documentation',
    type: 'ongoing',
    sla: null,
    specifics: 'GitHub Actions captures every deploy: who triggered, when, what commit SHA, which environment. SYS_DEPLOY events logged to audit_logs collection.',
    documents: [
      { templateId: '13.3-change-management-policy', section: '§9', language: 'Deploy logging captures who, when, what commit, which environment.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['GitHub Actions deploy logging not yet configured'],
    verification: {
      belt: 'GitHub Actions workflow logs every deploy automatically',
      suspenders: 'Audit log query returns SYS_DEPLOY events matching GitHub Actions logs',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['deploy log', 'audit trail', 'commit SHA', 'SYS_DEPLOY'],
    appearsInTemplates: ['13.3-change-management-policy'],
  },

  // ── 13.4 Data Classification Policy ──

  {
    id: 'CMT-091',
    scopeActivities: ['S-015'],
    requirements: ['REQ-061'],
    obligation: '',
    commitment: 'All Firestore collections classified by data level (PHI/PII/Confidential/Public).',
    category: 'compliance_documentation',
    type: 'ongoing',
    sla: null,
    specifics: 'Every Firestore collection is assigned a data classification level. PHI collections get highest protection. Classification map maintained and updated when new collections are created.',
    documents: [
      { templateId: '13.4-data-classification-policy', section: '§3', language: 'Firestore collection classification map.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['No formal classification map document'],
    verification: {
      belt: 'Data classification map reviewed when Firestore schema changes',
      suspenders: 'Annual data classification review covers all collections',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['data classification', 'PHI', 'PII', 'Firestore collection'],
    appearsInTemplates: ['13.4-data-classification-policy'],
  },
  {
    id: 'CMT-092',
    scopeActivities: ['S-015'],
    requirements: ['REQ-061'],
    obligation: '',
    commitment: 'Data handling requirements enforced per classification level.',
    category: 'compliance_documentation',
    type: 'ongoing',
    sla: null,
    specifics: 'Each classification level has defined handling requirements for storage, transit, sharing, retention, and disposal. PHI: encrypted + logged + BAA required. PII: encrypted + consent required.',
    documents: [
      { templateId: '13.4-data-classification-policy', section: '§4', language: 'Data handling requirements by classification level.' },
    ],
    systemId: null,
    systemStatus: 'partial',
    systemGaps: ['Handling requirements exist but not formally documented per level'],
    verification: {
      belt: 'Firestore rules enforce access patterns per collection classification',
      suspenders: 'Annual audit verifies handling requirements match classification levels',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['handling requirements', 'encryption', 'access control', 'classification level'],
    appearsInTemplates: ['13.4-data-classification-policy'],
  },

  // ── 13.5 Encryption & Key Management Policy ──

  {
    id: 'CMT-093',
    scopeActivities: ['S-015'],
    requirements: ['REQ-061'],
    obligation: '',
    commitment: 'AES-256 encryption at rest for all Firestore data.',
    category: 'safety',
    type: 'ongoing',
    sla: null,
    specifics: 'All Firestore data encrypted at rest using AES-256 with Google-managed keys. Automatic, zero configuration. Verified quarterly via GCP console.',
    documents: [
      { templateId: '13.5-encryption-key-management-policy', section: '§3', language: 'AES-256 encryption at rest for all Firestore data.' },
    ],
    systemId: null,
    systemStatus: 'built',
    systemGaps: [],
    verification: {
      belt: 'GCP enforces AES-256 automatically; no opt-out possible',
      suspenders: 'Quarterly verification checklist confirms encryption status in GCP console',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['AES-256', 'encryption at rest', 'Firestore', 'Google-managed keys'],
    appearsInTemplates: ['13.5-encryption-key-management-policy'],
  },
  {
    id: 'CMT-094',
    scopeActivities: ['S-015'],
    requirements: ['REQ-061'],
    obligation: '',
    commitment: 'TLS 1.2+ enforced for all data in transit.',
    category: 'safety',
    type: 'ongoing',
    sla: null,
    specifics: 'All Firebase connections use TLS 1.2+. Enforced by default at infrastructure level. No unencrypted communication possible.',
    documents: [
      { templateId: '13.5-encryption-key-management-policy', section: '§4', language: 'TLS 1.2+ enforced on all Firebase connections.' },
    ],
    systemId: null,
    systemStatus: 'built',
    systemGaps: [],
    verification: {
      belt: 'Firebase enforces TLS 1.2+ automatically; SSL Labs scan confirms',
      suspenders: 'Quarterly verification checklist includes SSL certificate validity check',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['TLS 1.2', 'encryption in transit', 'SSL', 'HTTPS'],
    appearsInTemplates: ['13.5-encryption-key-management-policy'],
  },
  {
    id: 'CMT-095',
    scopeActivities: ['S-015'],
    requirements: ['REQ-061'],
    obligation: '',
    commitment: 'API secrets never in client code or browser.',
    category: 'safety',
    type: 'ongoing',
    sla: null,
    specifics: 'All API keys (Anthropic, Stripe) stored in Firebase Secrets Manager. Accessed only by Cloud Functions server-side. Never bundled in client JavaScript.',
    documents: [
      { templateId: '13.5-encryption-key-management-policy', section: '§8', language: 'API keys stored in Firebase Secrets Manager. Never in client.' },
    ],
    systemId: null,
    systemStatus: 'built',
    systemGaps: [],
    verification: {
      belt: 'Cloud Function proxy architecture ensures keys are server-side only',
      suspenders: 'Code review checks for secrets in client code; automated scan in CI',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['secrets manager', 'API keys', 'server-side', 'Cloud Function proxy'],
    appearsInTemplates: ['13.5-encryption-key-management-policy'],
  },
  {
    id: 'CMT-096',
    scopeActivities: ['S-015'],
    requirements: ['REQ-061'],
    obligation: '',
    commitment: 'Quarterly encryption verification checklist completed.',
    category: 'compliance_documentation',
    type: 'periodic',
    sla: 'Quarterly',
    specifics: 'Every quarter: verify GCP encryption key rotation, verify SSL certificate validity, verify Firebase secrets currency, verify backup encryption, document verification date and reviewer.',
    documents: [
      { templateId: '13.5-encryption-key-management-policy', section: '§10', language: 'Quarterly verification checklist.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['No formal quarterly verification process'],
    verification: {
      belt: 'Calendar reminder triggers quarterly checklist; results documented',
      suspenders: 'SOC 2 evidence map tracks verification dates',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['quarterly verification', 'encryption', 'key rotation', 'SSL certificate'],
    appearsInTemplates: ['13.5-encryption-key-management-policy', '13.10-soc2-evidence-map'],
  },

  // ── 13.6 Vendor Management Policy ──

  {
    id: 'CMT-097',
    scopeActivities: ['S-017'],
    requirements: ['REQ-064'],
    obligation: '',
    commitment: 'Vendor risk assessment required before onboarding.',
    category: 'compliance_documentation',
    type: 'conditional',
    sla: null,
    specifics: 'Before onboarding any vendor that will access user data, conduct a security assessment: SOC 2 report, data handling practices, encryption standards, incident response, BAA readiness.',
    documents: [
      { templateId: '13.6-vendor-management-policy', section: '§3', language: 'Vendor assessment criteria for new vendors.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['No formal vendor assessment process'],
    verification: {
      belt: 'Vendor register shows assessment date and score for each vendor',
      suspenders: 'Quarterly vendor review confirms all vendors have current assessments',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['vendor assessment', 'risk assessment', 'onboarding', 'SOC 2 report'],
    appearsInTemplates: ['13.6-vendor-management-policy'],
  },
  {
    id: 'CMT-098',
    scopeActivities: ['S-017'],
    requirements: ['REQ-064'],
    obligation: '',
    commitment: 'BAA executed with all PHI-touching vendors.',
    category: 'regulatory_specific',
    type: 'ongoing',
    sla: null,
    specifics: 'Every vendor that accesses, processes, or stores PHI must have an executed BAA. Current vendors: Google Cloud/Firebase, Anthropic (Claude), OpenLoop, pharmacy partner.',
    documents: [
      { templateId: '13.6-vendor-management-policy', section: '§6', language: 'BAA required for all PHI-touching vendors.' },
    ],
    systemId: null,
    systemStatus: 'partial',
    systemGaps: ['GCP BAA not yet executed', 'Anthropic BAA not yet executed'],
    verification: {
      belt: 'Vendor register tracks BAA status and execution date for each vendor',
      suspenders: 'Quarterly vendor review flags vendors without current BAAs',
    },
    priority: 'P0',
    owner: 'attorney',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['BAA', 'business associate agreement', 'PHI', 'vendor'],
    appearsInTemplates: ['13.6-vendor-management-policy', '5.1-baa-llc-pc'],
  },
  {
    id: 'CMT-099',
    scopeActivities: ['S-017'],
    requirements: ['REQ-065'],
    obligation: '',
    commitment: 'Quarterly vendor review conducted and documented.',
    category: 'compliance_documentation',
    type: 'periodic',
    sla: 'Quarterly',
    specifics: 'Every quarter: review SOC 2 report currency, BAA expiration, service changes, incident history, SLA compliance. Document reviewer, date, findings, actions.',
    documents: [
      { templateId: '13.6-vendor-management-policy', section: '§9', language: 'Quarterly vendor review process.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['No formal quarterly vendor review process'],
    verification: {
      belt: 'Calendar reminder triggers quarterly review; results documented in vendor register',
      suspenders: 'SOC 2 evidence map tracks vendor review dates',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['quarterly vendor review', 'SOC 2 status', 'BAA', 'vendor register'],
    appearsInTemplates: ['13.6-vendor-management-policy', '13.10-soc2-evidence-map'],
  },

  // ── 13.7 Monitoring & Alerting Policy ──

  {
    id: 'CMT-100',
    scopeActivities: ['S-015'],
    requirements: ['REQ-058'],
    obligation: '',
    commitment: 'Uptime monitoring active on all production URLs.',
    category: 'sla_enforcement',
    type: 'ongoing',
    sla: null,
    specifics: 'UptimeRobot or equivalent pings all production URLs every 5 minutes. Alert on 2 consecutive failures (10 minutes). New brand URLs get monitoring automatically on launch.',
    documents: [
      { templateId: '13.7-monitoring-alerting-policy', section: '§2', language: 'HTTP uptime monitoring. Ping every 5 min. Alert on downtime.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['UptimeRobot not yet configured'],
    verification: {
      belt: 'UptimeRobot sends alert on downtime detection',
      suspenders: 'Monthly monitoring review confirms all URLs are being monitored',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['uptime monitoring', 'alert', 'downtime', 'production URL'],
    appearsInTemplates: ['13.7-monitoring-alerting-policy'],
  },
  {
    id: 'CMT-101',
    scopeActivities: ['S-015'],
    requirements: ['REQ-058'],
    obligation: '',
    commitment: 'Critical alerts responded to within 15 minutes.',
    category: 'sla_enforcement',
    type: 'conditional',
    sla: '15 minutes',
    specifics: 'Critical alerts (system outage, security breach) require acknowledgment within 15 minutes. High alerts: 1 hour during business hours. Medium: next business day.',
    documents: [
      { templateId: '13.7-monitoring-alerting-policy', section: '§4', language: 'Critical: 15-minute response SLA. High: 1 hour. Medium: next business day.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['No formal alerting/on-call system'],
    verification: {
      belt: 'Alert system timestamps response; SLA compliance tracked',
      suspenders: 'Monthly monitoring review reports on response time compliance',
    },
    priority: 'P0',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['15 minutes', 'critical alert', 'response SLA', 'escalation'],
    appearsInTemplates: ['13.7-monitoring-alerting-policy'],
  },
  {
    id: 'CMT-102',
    scopeActivities: ['S-015'],
    requirements: ['REQ-058'],
    obligation: '',
    commitment: 'Monthly monitoring review documented.',
    category: 'compliance_documentation',
    type: 'periodic',
    sla: 'Monthly',
    specifics: 'Monthly review of alert history, false positive rate, response times, and threshold appropriateness. Document reviewer, date, findings, and threshold adjustments.',
    documents: [
      { templateId: '13.7-monitoring-alerting-policy', section: '§9', language: 'Monthly monitoring review with documented findings.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['No monitoring review process'],
    verification: {
      belt: 'Calendar reminder triggers monthly review; results documented',
      suspenders: 'SOC 2 evidence map tracks monitoring review dates',
    },
    priority: 'P2',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['monthly review', 'monitoring', 'alert history', 'threshold'],
    appearsInTemplates: ['13.7-monitoring-alerting-policy', '13.10-soc2-evidence-map'],
  },

  // ── 13.8 Risk Assessment Policy ──

  {
    id: 'CMT-103',
    scopeActivities: ['S-018'],
    requirements: ['REQ-066'],
    obligation: '',
    commitment: 'Annual risk assessment conducted and documented.',
    category: 'compliance_documentation',
    type: 'periodic',
    sla: 'Annually',
    specifics: 'Full risk register review annually: identify new risks, re-score existing risks, evaluate control effectiveness, update treatment plans. Document assessor, date, findings, changes.',
    documents: [
      { templateId: '13.8-risk-assessment-policy', section: '§7', language: 'Annual risk assessment process.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['No formal risk assessment process or risk register'],
    verification: {
      belt: 'Annual risk assessment report produced and filed',
      suspenders: 'SOC 2 evidence map tracks assessment date; auditor reviews risk register',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['annual risk assessment', 'risk register', 'risk scoring', 'treatment plan'],
    appearsInTemplates: ['13.8-risk-assessment-policy', '13.10-soc2-evidence-map'],
  },
  {
    id: 'CMT-104',
    scopeActivities: ['S-018'],
    requirements: ['REQ-066', 'REQ-067'],
    obligation: '',
    commitment: 'High/Critical risks have active treatment plans.',
    category: 'compliance_documentation',
    type: 'ongoing',
    sla: null,
    specifics: 'Any risk scored High (12-19) or Critical (20-25) must have an active treatment plan with owner, timeline, and success criteria. Critical risks require immediate action.',
    documents: [
      { templateId: '13.8-risk-assessment-policy', section: '§5', language: 'Risk treatment options: mitigate, transfer, accept, avoid.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['No risk register or treatment plans'],
    verification: {
      belt: 'Risk register flags untreated High/Critical risks',
      suspenders: 'Annual risk assessment reviews treatment plan effectiveness',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['risk treatment', 'high risk', 'critical risk', 'treatment plan'],
    appearsInTemplates: ['13.8-risk-assessment-policy'],
  },

  // ── 13.9 Acceptable Use Policy ──

  {
    id: 'CMT-105',
    scopeActivities: ['S-019'],
    requirements: ['REQ-068'],
    obligation: '',
    commitment: 'All workforce members sign Acceptable Use Policy.',
    category: 'compliance_documentation',
    type: 'conditional',
    sla: null,
    specifics: 'Every employee, contractor, and vendor with system access must sign the AUP before receiving access. Signed acknowledgment kept on file.',
    documents: [
      { templateId: '13.9-acceptable-use-policy', section: '§Acknowledgment', language: 'I have read and understand the Acceptable Use Policy.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['No AUP distribution or signature collection process'],
    verification: {
      belt: 'Onboarding checklist includes AUP signature; filed in personnel records',
      suspenders: 'Quarterly access review verifies AUP signature on file for all active users',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['acceptable use', 'signed', 'acknowledgment', 'workforce'],
    appearsInTemplates: ['13.9-acceptable-use-policy'],
  },
  {
    id: 'CMT-106',
    scopeActivities: ['S-019'],
    requirements: ['REQ-068'],
    obligation: '',
    commitment: 'PHI only accessed through authorized platform interfaces.',
    category: 'safety',
    type: 'ongoing',
    sla: null,
    specifics: 'PHI is never copied to local devices, personal email, or unauthorized cloud services. Accessed only through admin dashboard and Cloud Functions. Report any suspected PHI exposure within 4 hours.',
    documents: [
      { templateId: '13.9-acceptable-use-policy', section: '§4', language: 'PHI only accessed through authorized platform interfaces.' },
    ],
    systemId: null,
    systemStatus: 'built',
    systemGaps: [],
    verification: {
      belt: 'Firestore rules prevent PHI access outside authorized paths; audit logging tracks all access',
      suspenders: 'AUP acknowledgment confirms workforce understanding of PHI handling rules',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['PHI', 'authorized interfaces', 'no personal devices', 'audit logging'],
    appearsInTemplates: ['13.9-acceptable-use-policy', '13.2-access-control-policy'],
  },
  {
    id: 'CMT-107',
    scopeActivities: ['S-019'],
    requirements: ['REQ-068'],
    obligation: '',
    commitment: 'Security incidents reported within 4 hours of discovery.',
    category: 'notification',
    type: 'conditional',
    sla: '4 hours',
    specifics: 'All workforce members must report suspected security incidents, policy violations, suspicious activity, or PHI exposures within 4 hours of discovery to compliance@hemologix.com.',
    documents: [
      { templateId: '13.9-acceptable-use-policy', section: '§9', language: 'Report suspected security incidents within 4 hours.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['No incident reporting workflow'],
    verification: {
      belt: 'Incident report form with timestamp; escalation to compliance officer',
      suspenders: 'Quarterly review of incident reports for timeliness compliance',
    },
    priority: 'P0',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['4 hours', 'incident reporting', 'security incident', 'compliance'],
    appearsInTemplates: ['13.9-acceptable-use-policy', '8.7-incident-response-runbook'],
  },

  // ── 13.10 SOC 2 Evidence Map ──

  {
    id: 'CMT-108',
    scopeActivities: ['S-015', 'S-016', 'S-017', 'S-018', 'S-019'],
    requirements: ['REQ-070'],
    obligation: '',
    commitment: 'SOC 2 evidence collected on scheduled cadence (monthly/quarterly/annually).',
    category: 'compliance_documentation',
    type: 'periodic',
    sla: 'Per schedule',
    specifics: 'Evidence collection follows defined schedule: access review logs (quarterly), deploy logs (monthly), monitoring reports (monthly), risk assessment (annually), vendor reviews (quarterly), training records (annually).',
    documents: [
      { templateId: '13.10-soc2-evidence-map', section: '§Evidence Collection Schedule', language: 'Monthly, quarterly, and annual evidence collection schedule.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['No evidence collection process or storage'],
    verification: {
      belt: 'Calendar reminders trigger evidence collection at each interval',
      suspenders: 'Pre-audit readiness review checks evidence completeness',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['evidence collection', 'monthly', 'quarterly', 'annually', 'SOC 2'],
    appearsInTemplates: ['13.10-soc2-evidence-map'],
  },
  {
    id: 'CMT-109',
    scopeActivities: ['S-015'],
    requirements: ['REQ-070'],
    obligation: '',
    commitment: 'Immutable audit trail — no role can update or delete audit log entries.',
    category: 'compliance_documentation',
    type: 'ongoing',
    sla: null,
    specifics: 'Firestore security rules enforce audit log immutability. Allow create only (from Cloud Functions). No update, no delete — not even for owner role. Annotate via new entries referencing the original.',
    documents: [
      { templateId: '13.10-soc2-evidence-map', section: '§Security (CC)', language: 'Immutable audit trail. No update or delete on audit_logs collection.' },
      { templateId: '13.1-information-security-policy', section: '§4', language: 'Immutable audit trail.' },
    ],
    systemId: null,
    systemStatus: 'partial',
    systemGaps: ['logAudit() exists but Firestore rules not yet enforcing immutability'],
    verification: {
      belt: 'Firestore rules block update/delete on audit_logs collection',
      suspenders: 'Penetration test attempts to modify audit entries; should fail',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['immutable', 'audit trail', 'no delete', 'no update', 'audit_logs'],
    appearsInTemplates: ['13.10-soc2-evidence-map', '13.1-information-security-policy'],
  },

  // ── Cross-cutting: Audit Logging (spans multiple SOC 2 docs) ──

  {
    id: 'CMT-110',
    scopeActivities: ['S-015'],
    requirements: ['REQ-058', 'REQ-059'],
    obligation: '',
    commitment: 'All 6 audit event categories logged to audit_logs collection.',
    category: 'compliance_documentation',
    type: 'ongoing',
    sla: null,
    specifics: 'Audit logging covers: Authentication (AUTH_*), Data Access (DATA_*), Data Modification (DATA_*), AI Interactions (AI_*), Admin Actions (ADMIN_*), System Events (SYS_*). Schema: timestamp, userId, userRole, action, resourceType, resourceId, ipAddress, userAgent, details, outcome.',
    documents: [
      { templateId: '13.1-information-security-policy', section: '§4', language: 'Six audit event categories logged.' },
      { templateId: '13.7-monitoring-alerting-policy', section: '§7', language: 'Log aggregation and retention.' },
    ],
    systemId: null,
    systemStatus: 'partial',
    systemGaps: ['logAudit() covers AI and document events; missing auth events, data access events, system events'],
    verification: {
      belt: 'logAudit() fires on every auditable action; Firestore audit_logs collection populated',
      suspenders: 'Monthly audit log completeness check verifies all 6 categories have recent entries',
    },
    priority: 'P0',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['audit logging', 'AUTH_LOGIN', 'DATA_VIEW', 'AI_PROMPT', 'ADMIN_CONFIG', 'SYS_DEPLOY'],
    appearsInTemplates: ['13.1-information-security-policy', '13.7-monitoring-alerting-policy', '8.1-data-ops-spec'],
  },
  {
    id: 'CMT-111',
    scopeActivities: ['S-015', 'S-019'],
    requirements: ['REQ-069'],
    obligation: '',
    commitment: 'Annual HIPAA and security awareness training completed by all workforce.',
    category: 'compliance_documentation',
    type: 'periodic',
    sla: 'Annually',
    specifics: 'HIPAA Privacy and Security training annually for all workforce. Breach reporting procedures annually. Incident response training semi-annually for key personnel. Completion tracked and documented.',
    documents: [
      { templateId: '8.9-compliance-training-schedule', section: '§Training Matrix', language: 'HIPAA Privacy and Security — all workforce — annually.' },
      { templateId: '13.9-acceptable-use-policy', section: '§10', language: 'Annual security awareness training required.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['No training platform or tracking system'],
    verification: {
      belt: 'Training completion records maintained; overdue notifications sent automatically',
      suspenders: 'Annual compliance training audit verifies 100% completion',
    },
    priority: 'P1',
    owner: 'admin',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['annual training', 'HIPAA training', 'security awareness', 'training completion'],
    appearsInTemplates: ['8.9-compliance-training-schedule', '13.9-acceptable-use-policy', '13.10-soc2-evidence-map'],
  },
  {
    id: 'CMT-112',
    scopeActivities: ['S-015', 'S-016', 'S-017', 'S-018', 'S-019'],
    requirements: ['REQ-070'],
    obligation: '',
    commitment: 'Brand launch blocked until all 15 compliance checklist items pass.',
    category: 'entity_integrity',
    type: 'conditional',
    sla: null,
    specifics: 'No brand goes live without passing all 15 compliance checks: brand context complete, entity verified, MD assigned, states populated, formulary valid, 73+ docs generated, platform score pass, brand score pass, cross-doc consistency, consent flow functional, audit logging active, Firestore rules deployed, monitoring active, BAAs executed, admin sign-off.',
    documents: [
      { templateId: '13.10-soc2-evidence-map', section: '§Pre-Audit Timeline', language: 'Brand launch compliance checklist — 15 items.' },
      { templateId: '13.1-information-security-policy', section: '§5', language: 'Platform controls inherited by all brands.' },
    ],
    systemId: null,
    systemStatus: 'missing',
    systemGaps: ['Brand launch checklist not built', 'Multi-brand architecture not implemented'],
    verification: {
      belt: 'Automated compliance checklist runs before brand launch; blocks deployment if any check fails',
      suspenders: 'Manual admin sign-off required as final gate',
    },
    priority: 'P0',
    owner: 'system',
    status: 'planned',
    slaConflicts: [],
    qualityGateKeywords: ['brand launch', 'compliance checklist', '15 checks', 'launch gate'],
    appearsInTemplates: ['13.10-soc2-evidence-map', '13.1-information-security-policy'],
  },

  // ════════════════════════════════════════════
  // MCP SERVER — TOOL COMMITMENTS
  // ════════════════════════════════════════════

  {
    id: 'MCP-011',
    scopeActivities: ['S-003'],
    requirements: [],
    obligation: '',
    commitment: 'Correlations tool exposes clinically validated biomarker correlation data via MCP. Population-level only, no PHI.',
    category: 'safety',
    type: 'ongoing',
    sla: null,
    specifics: 'hemologix_get_correlations returns 30 clinically validated biomarker correlations including relationship direction (inverse/positive/conditional), strength (strong/moderate/weak), and clinical interpretation rules. Data sourced from Master Formulator evidence library, validated by Dr. Calapai. No patient-specific data. No authentication required. Added during MCP server build — correlation data was loaded but unexposed.',
    documents: [],
    systemId: null,
    systemStatus: 'built',
    systemGaps: [],
    verification: {
      belt: 'Automated smoke tests verify correlation tool returns correct data shapes and filter logic (5 test cases)',
      suspenders: 'Admin MCP dashboard provides interactive tool explorer for manual verification',
    },
    priority: 'P1',
    owner: 'system',
    status: 'active',
    slaConflicts: [],
    qualityGateKeywords: ['correlations', 'biomarker', 'MCP', 'population-level'],
    appearsInTemplates: [],
  },
];
